import {useCallback, useEffect, useRef} from "react";

import {useQueryClient} from "@tanstack/react-query";

import {useSocket} from "@/src/modules/core/context/socket";

/** Same Pusher event names as notification feed — Laravel `BroadcastNotificationCreated`. */
const NOTIFICATION_ARRIVAL_EVENT_NAMES = [
  "Illuminate\\Notifications\\Events\\BroadcastNotificationCreated",
  ".Illuminate\\Notifications\\Events\\BroadcastNotificationCreated",
  "notifications.created",
  ".notifications.created",
  "notification.created",
] as const;

const BRIEFING_BOARD_TOPIC = "briefing_board";
const BRIEFING_BOARD_CREATED_TYPE = "briefing_board.created";
const BRIEFING_BOARD_ACK_TYPE = "briefing_board.ack_required";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function isBriefingBoardCreatedNotification(payload: unknown): boolean {
  const root = asRecord(payload);
  if (!root) {
    return false;
  }

  const data = asRecord(root.data) ?? {};
  const topic = asString(root.topic) ?? asString(data.topic);
  const type = asString(root.type) ?? asString(data.type);

  return (
    topic === BRIEFING_BOARD_TOPIC &&
    (type === BRIEFING_BOARD_CREATED_TYPE || type === BRIEFING_BOARD_ACK_TYPE)
  );
}

type UseBriefingPendingRealtimeParams = {
  enabled?: boolean;
};

/** Refetch pending briefings when a new briefing notification arrives or socket reconnects. */
export function useBriefingPendingRealtime({enabled = false}: UseBriefingPendingRealtimeParams = {}) {
  const queryClient = useQueryClient();
  const {socket} = useSocket();
  const hasSeenInitialOpen = useRef(false);

  const refetchPending = useCallback(() => {
    void queryClient.refetchQueries({queryKey: ["briefingboard-pending"], type: "all"});
  }, [queryClient]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onArrival = (event: Event) => {
      const payload = (event as CustomEvent<unknown>).detail;
      if (isBriefingBoardCreatedNotification(payload)) {
        refetchPending();
      }
    };

    NOTIFICATION_ARRIVAL_EVENT_NAMES.forEach((eventName) => {
      window.addEventListener(eventName, onArrival);
    });

    return () => {
      NOTIFICATION_ARRIVAL_EVENT_NAMES.forEach((eventName) => {
        window.removeEventListener(eventName, onArrival);
      });
    };
  }, [enabled, refetchPending]);

  useEffect(() => {
    if (!enabled || !socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!hasSeenInitialOpen.current) {
      hasSeenInitialOpen.current = true;
      return;
    }

    refetchPending();
  }, [enabled, socket, refetchPending]);
}
